import { memo, useMemo } from "react";
import { tileUrl, TILE_MAX_ZOOM, TILE_MIN_ZOOM, TILE_SIZE } from "@/data/maps.ts";
import {
  tileZoomFor,
  visibleTiles,
  type Camera,
  type ProjectionConfig,
  type Viewport,
} from "@/engine/projection.ts";

interface TileLayerProps {
  readonly mapId: string;
  readonly camera: Camera;
  readonly viewport: Viewport;
  readonly config: ProjectionConfig;
}

/**
 * The map imagery.
 *
 * Two levels are drawn at once: the current one, and a coarser one underneath
 * it. The coarse level is already cached from when the user was zoomed out, so
 * it paints instantly and fills the gaps while the sharp tiles stream in. The
 * alternative — blank squares during every zoom — reads as a broken map.
 *
 * Map imagery is WARDOGS game content, (c) BULKHEAD / Team17.
 */
function TileLayerImpl({
  mapId,
  camera,
  viewport,
  config,
}: TileLayerProps): React.ReactElement {
  const zoom = tileZoomFor(camera, TILE_MAX_ZOOM, TILE_MIN_ZOOM, TILE_SIZE, config.worldSize);

  const tiles = useMemo(
    () => visibleTiles(camera, viewport, zoom, config.worldSize),
    [camera, viewport, zoom, config.worldSize],
  );

  // One level coarser: a quarter of the tiles, already in cache, as a backdrop.
  const backdropZoom = Math.max(TILE_MIN_ZOOM, zoom - 1);
  const backdropTiles = useMemo(
    () =>
      backdropZoom === zoom
        ? []
        : visibleTiles(camera, viewport, backdropZoom, config.worldSize),
    [camera, viewport, backdropZoom, zoom, config.worldSize],
  );

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
      {backdropTiles.map((tile) => (
        <img
          key={`b-${tile.zoom}-${tile.x}-${tile.y}`}
          src={tileUrl(mapId, tile.zoom, tile.x, tile.y)}
          alt=""
          draggable={false}
          loading="eager"
          decoding="async"
          // Tailwind's preflight caps <img> at `max-width: 100%` of its
          // containing block. A tile zoomed in past native resolution is
          // routinely wider than the viewport, so without `max-w-none` the
          // browser clips its rendered width (not height) back down to the
          // container — leaving a tall black gap between tile columns while
          // the neighbouring tile's height renders at full size.
          className="absolute max-w-none select-none"
          style={{
            left: `${tile.left}px`,
            top: `${tile.top}px`,
            width: `${tile.size}px`,
            height: `${tile.size}px`,
          }}
        />
      ))}

      {tiles.map((tile) => (
        <img
          key={`${tile.zoom}-${tile.x}-${tile.y}`}
          src={tileUrl(mapId, tile.zoom, tile.x, tile.y)}
          alt=""
          draggable={false}
          loading="eager"
          decoding="async"
          className="absolute max-w-none select-none"
          style={{
            left: `${tile.left}px`,
            top: `${tile.top}px`,
            width: `${tile.size}px`,
            height: `${tile.size}px`,
          }}
        />
      ))}
    </div>
  );
}

export const TileLayer = memo(TileLayerImpl);
